import { useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '../lib/cn';
import {
  getThemePreference,
  resolveTheme,
  setThemePreference,
  type Theme,
  type ThemePreference,
} from '../lib/theme';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from './Menu';

const ICONS: Record<ThemePreference, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const LABELS: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const [pref, setPref] = useState<ThemePreference>('system');
  const [resolved, setResolved] = useState<Theme>('light');

  useEffect(() => {
    const initial = getThemePreference();
    setPref(initial);
    setResolved(resolveTheme(initial));
  }, []);

  function choose(next: ThemePreference) {
    setPref(next);
    setResolved(resolveTheme(next));
    setThemePreference(next);
  }

  const TriggerIcon = ICONS[resolved === 'dark' ? 'dark' : 'light'];

  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          aria-label="Theme"
          title={`Theme: ${LABELS[pref]}`}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-sm border border-border-soft bg-surface text-ink-secondary',
            'transition-[border-color,color,background-color] duration-fast hover:border-border-strong hover:text-ink',
            'focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-focus',
            className,
          )}
        >
          <TriggerIcon className="h-4 w-4" aria-hidden />
        </button>
      </MenuTrigger>
      <MenuContent align="end" sideOffset={8} className="min-w-[140px]">
        {(Object.keys(LABELS) as ThemePreference[]).map((key) => {
          const Icon = ICONS[key];
          const active = pref === key;
          return (
            <MenuItem
              key={key}
              onSelect={() => choose(key)}
              className={cn(active && 'text-primary')}
            >
              <Icon className="h-4 w-4" aria-hidden />
              <span className="flex-1">{LABELS[key]}</span>
              {active ? (
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
              ) : null}
            </MenuItem>
          );
        })}
      </MenuContent>
    </Menu>
  );
}
